import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts'
import { SSRMeasures } from '@confluence/action-measures/entry-points/SSRMeasures.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { isUnauthorizedError } from '@confluence/unauthorized-error-boundary';
import { markErrorAsHandled } from '@confluence/graphql-error-processor/entry-points/GraphqlErrorProcessor.ts';

import type { SessionDataType } from './SessionDataTypes.ts';
import SiteInformationQuery, { type SiteInformationQuery$data, type SiteInformationQuery$variables } from './__generated__/SiteInformationQuery.graphql.ts';

declare global {
	interface Window {
		__SITEINFORMATION_PROMISE__: Promise<SessionDataType> | null;
		__SITEINFORMATION__:
			| Promise<SiteInformationQuery$data | null>
			| SiteInformationQuery$data
			| Promise<null>
			| null;
	}
}

export async function loadSessionData(): Promise<SiteInformationQuery$data | null> {
	// __SITEINFORMATION__ is either actual session data
	// or the promise that is resolving the session data
	// In the case when page is rendered by SSR, the __SITEINFORMATION__ will be populated with JSON data.
	if (window['__SITEINFORMATION__']) {
		return window['__SITEINFORMATION__'];
	}

	window['__SITEINFORMATION__'] = SSRMeasures.run(`ssr-app/prepare/preloadQuery/fetch:SiteInformationQuery`, async () =>
		queryInterop<SiteInformationQuery$data, SiteInformationQuery$variables>({
			query: SiteInformationQuery,
			errorPolicy: 'all',
			fetchPolicy: 'cache-first',
			context: {
				single: false,
				allowOnExternalPage: true,
			},
		}),
	)
		.then((result) => {
			const errors = result.errors ?? [];
			if (errors.length > 0) {
				errors.forEach((e) => {
					if (isUnauthorizedError(e) && !!result?.data?.tenant?.cloudId) {
						markErrorAsHandled(e);
					} else {
						getMonitoringClient().submitError(e, {
							isSiteInformationError: true,
							partialFailure: true,
							attribution: 'FATAL',
						});
					}
				});
			}
			return result.data;
		})
		.catch((error) => {
			if (Array.isArray(error)) {
				error.forEach((e) =>
					getMonitoringClient().submitError(e, {
						isSiteInformationError: true,
						partialFailure: false,
						attribution: 'FATAL',
					}),
				);
			} else {
				getMonitoringClient().submitError(error, {
					isSiteInformationError: true,
					partialFailure: false,
					attribution: 'FATAL',
				});
			}

			return null;
		});

	return window['__SITEINFORMATION__'];
}
